import { Injectable } from '@angular/core';
import { CollabDocDeleteEvent } from '@common/collab/events/collab-doc-delete.event';
import { CollabDocMergeEvent } from '@common/collab/events/collab-doc-merge.event';
import { CollabDocRenameEvent } from '@common/collab/events/collab-doc-rename.event';
import { GitStatus } from '@common/git/types/git-status.type';
import { WorkspaceFilesGetResponse } from '@common/sockets/models/workspace-files-get.model';
import { WorkspaceSession } from '@common/sockets/workspace-session';
import { cache } from '@common/util/cache.operator';
import { WorkspaceFile } from '@common/workspace/models/workspace-file.model';
import { WorkspacePullFileState } from '@common/workspace/models/workspace-pull-file-outcome.model';
import { WorkspacePullState } from '@common/workspace/models/workspace-pull-state.model';
import { ApiService } from '@portal-core/auth/services/api.service';
import { AuthService } from '@portal-core/auth/services/auth.service';
import { BranchesService } from '@portal-core/branches/services/branches.service';
import { OnlineService } from '@portal-core/general/services/online.service';
import { LicenseUsersService } from '@portal-core/license-users/services/license-users.service';
import { LoadingState } from '@portal-core/util/loading-state';
import { WorkspaceFilesService } from '@portal-core/workspaces/services/workspace-files/workspace-files.service';
import { BehaviorSubject, distinctUntilChanged, map, merge, Observable, of, skip, Subject, Subscription, switchMap } from 'rxjs';

interface WorkspaceSessionParams {
  branchName: string;
  licenseId: number;
  projectId: number;
}

@Injectable({
  providedIn: 'root'
})
export class WorkspaceSessionService {
  /** The subscription for the online status. */
  private onlineSubscription: Subscription;
  /** Subscriptions for tracking the workspace session's error and connected state. */
  private errorSubscription: Subscription;
  private connectedSubscription: Subscription;
  /** Subscription that triggers fetch on commit-id change. */
  private autoFetchSubscription: Subscription;
  private workspaceSession: WorkspaceSession;

  /** Tracks the active session's params (or null when no session is active). Drives commitId$. */
  private activeParams$: BehaviorSubject<WorkspaceSessionParams | null> = new BehaviorSubject<WorkspaceSessionParams | null>(null);

  /** Tracks the workspace socket connection + files loading lifecycle. */
  workspaceLoadingState: LoadingState<string> = new LoadingState<string>();

  private docDeletedSubject: Subject<CollabDocDeleteEvent> = new Subject<CollabDocDeleteEvent>();
  private docMergedSubject: Subject<CollabDocMergeEvent> = new Subject<CollabDocMergeEvent>();
  private fileDeletedSubject: Subject<WorkspaceFile> = new Subject<WorkspaceFile>();
  private fileInsertedSubject: Subject<WorkspaceFile> = new Subject<WorkspaceFile>();
  private fileRenamedSubject: Subject<CollabDocRenameEvent> = new Subject<CollabDocRenameEvent>();
  private fileUpdatedSubject: Subject<WorkspaceFile> = new Subject<WorkspaceFile>();
  private connectedSubject: Subject<void> = new Subject<void>();
  private pullStartedSubject: Subject<WorkspacePullState> = new Subject<WorkspacePullState>();
  private pullProgressSubject: Subject<WorkspacePullState> = new Subject<WorkspacePullState>();
  private pullCompletedSubject: Subject<WorkspacePullState> = new Subject<WorkspacePullState>();
  private pullFailedSubject: Subject<WorkspacePullState> = new Subject<WorkspacePullState>();
  private pullFileCompletedSubject: Subject<{ filePath: string; fileOutcome: WorkspacePullFileState }> = new Subject<{ filePath: string; fileOutcome: WorkspacePullFileState }>();

  /**
   * File paths of collab docs in the active session that have remote changes, keyed by path
   * with each entry's value being the remote git status (`'added' | 'modified' | 'deleted'`).
   * Carrying the status alongside each path lets the pull dialog render a per-row status badge
   * without cross-referencing `workspace_files` (which is a dirty-state mirror and doesn't
   * contain entries for committed-then-remotely-deleted files). Updated on each fetch — the
   * server already computes the per-doc remote git status as part of the refresh, so the data
   * costs nothing extra to project through the response.
   */
  filePathsWithRemoteChanges$: BehaviorSubject<Map<string, GitStatus>> = new BehaviorSubject<Map<string, GitStatus>>(new Map());

  docDeleted$: Observable<CollabDocDeleteEvent> = this.docDeletedSubject.asObservable();
  /** Emits when an open collab doc in the active session was merged with an incoming commit
   * (auto-pull or manual pull). Distinct from `fileUpdated$`, which tracks workspace_file row
   * changes — a merge of a clean doc may not produce a `fileUpdated$` emission. */
  docMerged$: Observable<CollabDocMergeEvent> = this.docMergedSubject.asObservable();
  fileDeleted$: Observable<WorkspaceFile> = this.fileDeletedSubject.asObservable();
  fileInserted$: Observable<WorkspaceFile> = this.fileInsertedSubject.asObservable();
  fileRenamed$: Observable<CollabDocRenameEvent> = this.fileRenamedSubject.asObservable();
  fileUpdated$: Observable<WorkspaceFile> = this.fileUpdatedSubject.asObservable();
  /** Emits every time the workspace socket connects (including the first connection). */
  connected$: Observable<void> = this.connectedSubject.asObservable();
  /** Workspace pull progress events, sourced from collab-authority and forwarded by collab-workspace. */
  pullStarted$: Observable<WorkspacePullState> = this.pullStartedSubject.asObservable();
  pullProgress$: Observable<WorkspacePullState> = this.pullProgressSubject.asObservable();
  pullCompleted$: Observable<WorkspacePullState> = this.pullCompletedSubject.asObservable();
  pullFailed$: Observable<WorkspacePullState> = this.pullFailedSubject.asObservable();
  /** Emits once per file as its pull pipeline completes, with the per-file outcome payload. */
  pullFileCompleted$: Observable<{ filePath: string; fileOutcome: WorkspacePullFileState }> = this.pullFileCompletedSubject.asObservable();

  /** Emits the latest commit id of the active session's branch (driven by polling/refresh in BranchesService). */
  commitId$: Observable<string> = this.activeParams$.pipe(
    switchMap(params => params ? this.branchesService.getBranchFromProjectId$(params.projectId, params.branchName) : of(null)),
    map(branch => branch?.Commit?.id),
    distinctUntilChanged(),
    cache()
  );

  /** True when the active session has any collab doc with remote changes. */
  hasRemoteChanges$: Observable<boolean> = this.filePathsWithRemoteChanges$.pipe(
    map(remoteChanges => remoteChanges.size > 0),
    distinctUntilChanged()
  );

  /**
   * Emits whenever the workspace file tree changes.
   * E.g. when the branch commit advances or when a file is deleted, renamed, or inserted.
   */
  fileTreeChange$: Observable<unknown> = merge(
    this.commitId$.pipe(skip(1)), // skip the initial value to process only changes
    this.fileDeleted$,
    this.fileRenamed$,
    this.fileInserted$,
  );

  /** The projectId of the current workspace session, or undefined if no session is active. */
  get projectId(): number | undefined {
    return this.workspaceSession?.projectId;
  }

  /** The branchName of the current workspace session, or undefined if no session is active. */
  get branchName(): string | undefined {
    return this.workspaceSession?.branchName;
  }

  /** The licenseId of the current workspace session, or undefined if no session is active. */
  get licenseId(): number | undefined {
    return this.activeParams$.value?.licenseId;
  }

  constructor(
    private apiService: ApiService,
    private authService: AuthService,
    private branchesService: BranchesService,
    private licenseUsersService: LicenseUsersService,
    private onlineService: OnlineService,
    private workspaceFilesService: WorkspaceFilesService
  ) {
    // Keep the collab session up to date on the online status of the user
    this.onlineSubscription = this.onlineService.online$.subscribe(online => {
      if (this.workspaceSession) {
        this.workspaceSession.offline = !online;
      }
    });
  }

  public reset(licenseId: number, projectId: number, branchName: string) {
    // Preserve filePathsWithRemoteChanges$ across same-params resets (e.g. page-level reloads) so the
    // pull bar doesn't flash off and on while fetch refills it. Only clear when switching workspaces.
    const previous = this.activeParams$.value;
    const sameWorkspace = previous?.projectId === projectId && previous?.branchName === branchName;

    this.destroyWorkspaceSession();
    if (!sameWorkspace) {
      this.filePathsWithRemoteChanges$.next(new Map());
    }
    this.createWorkspaceSession(licenseId, projectId, branchName);
  }

  public clear() {
    this.destroyWorkspaceSession();
    this.filePathsWithRemoteChanges$.next(new Map());
  }

  public reload() {
    if (this.workspaceSession) {
      const params = this.activeParams$.value;
      if (this.workspaceSession.connected) {
        this.workspaceSession.reload();
      } else if (params) {
        this.reset(params.licenseId, params.projectId, params.branchName);
      }
    }
  }

  /**
   * Fetches remote git statuses for every collab doc in the active session and stores the resulting
   * file paths with remote changes. No-op if no session is active.
   */
  public fetch() {
    const params = this.activeParams$.value;
    if (!params) {
      return;
    }

    this.workspaceFilesService.fetchWorkspace$(params.projectId, params.branchName).subscribe({
      next: result => {
        const filePaths = result?.Result?.filePathsWithRemoteChanges ?? {};
        this.filePathsWithRemoteChanges$.next(new Map(Object.entries(filePaths)));
      },
      error: () => {
        // Swallow; the next fetch (commit/connect/manual) will retry. Keep prior state.
      }
    });
  }

  /**
   * Clears tracked remote-change paths after a successful pull. The next external commit will repopulate
   * via fetch.
   */
  public clearRemoteChanges() {
    this.filePathsWithRemoteChanges$.next(new Map());
  }

  /**
   * Replaces the tracked remote-change paths + statuses with the given map. Used by the initial
   * fetch bootstrap to seed the state from the server's authoritative response.
   *
   * Do **not** use this from pull-completion code: a fetch may have discovered new pullable files
   * mid-pull that aren't in the pull's snapshot, and a wholesale replace would drop them. Use
   * {@link removeRemoteChanges} to subtract only the paths the pull actually resolved.
   */
  public setRemoteChanges(remoteChanges: ReadonlyMap<string, GitStatus> | Record<string, GitStatus>) {
    const next = remoteChanges instanceof Map
      ? new Map(remoteChanges)
      : new Map(Object.entries(remoteChanges));
    this.filePathsWithRemoteChanges$.next(next);
  }

  /**
   * Removes the given paths from the tracked remote-change map, leaving any other paths intact.
   * Used after a pull to drop the files that the pull actually resolved (merged / soft-deleted /
   * pending-add) without disturbing files that a concurrent fetch discovered mid-pull but that
   * weren't part of this pull's snapshot.
   */
  public removeRemoteChanges(filePaths: Iterable<string>) {
    const current = this.filePathsWithRemoteChanges$.value;
    const next = new Map(current);
    let changed = false;
    for (const path of filePaths) {
      if (next.delete(path)) {
        changed = true;
      }
    }
    if (changed) {
      this.filePathsWithRemoteChanges$.next(next);
    }
  }

  private createWorkspaceSession(licenseId: number, projectId: number, branchName: string) {
    this.workspaceLoadingState.update(true);
    this.activeParams$.next({ licenseId, projectId, branchName });

    this.workspaceSession = new WorkspaceSession({
      socketBaseUrl: this.apiService.collabWorkspaceUrl,
      offline: !this.onlineService.online,
      refreshToken$: () => this.authService.refreshToken$(),
      branchName,
      projectId
    });

    // Track connection errors to surface them to the UI
    this.errorSubscription = this.workspaceSession.error$.subscribe(error => {
      if (error) {
        this.workspaceLoadingState.update(false, 'Unable to connect to workspace services. Reconnecting...');
      }
    });

    // When the workspace session connects, resume loading state if there was an error and notify subscribers
    this.connectedSubscription = this.workspaceSession.connected$.subscribe(connected => {
      if (connected) {
        this.connectedSubject.next();
      }
    });

    // Auto-fetch on every connection (initial + reconnect) and on every commit-id change for the active session
    this.autoFetchSubscription = new Subscription();
    this.autoFetchSubscription.add(this.connectedSubject.subscribe(() => this.fetch()));
    this.autoFetchSubscription.add(this.commitId$.subscribe(() => this.fetch()));

    this.workspaceSession.on('workspace.file.deleted', (workspaceFile: WorkspaceFile) => {
      this.workspaceFilesService.removeItems$([workspaceFile]);
      this.fileDeletedSubject.next(workspaceFile);
    });

    this.workspaceSession.on('workspace.file.inserted', (workspaceFile: WorkspaceFile) => {
      this.workspaceFilesService.addItems$([workspaceFile]);
      this.loadUsers([workspaceFile]);
      this.fileInsertedSubject.next(workspaceFile);
    });

    this.workspaceSession.on('workspace.file.updated', (workspaceFile: WorkspaceFile) => {
      this.workspaceFilesService.updateItems$([workspaceFile]);
      this.loadUsers([workspaceFile]);
      this.fileUpdatedSubject.next(workspaceFile);
    });

    // `workspace.doc.deleted` fires from collab-authority's pub/sub for explicit collab delete operations.
    // This is distinct from `workspace.file.deleted` (which fires for workspace_file row deletes — i.e. discards
    // of pending adds). Mirror the rename pattern: synchronously update `workspaceFilesService` before emitting
    // so `hasUncommittedDescendant` sees the post-delete state immediately.
    this.workspaceSession.on('workspace.doc.deleted', (event: CollabDocDeleteEvent) => {
      const existing = this.workspaceFilesService.getWorkspaceFile(event.projectId, event.branchName, event.filePath);
      if (existing) {
        this.workspaceFilesService.removeItems$([existing]);
      }

      this.dropFromRemoteChanges(event.filePath);
      this.docDeletedSubject.next(event);
    });

    // `workspace.doc.merged` fires from collab-authority's pub/sub when a doc was merged with an
    // incoming commit. Distinct from `workspace.file.updated` because a clean-doc merge may leave the
    // workspace_files row unchanged (no Mongo change stream, no `fileUpdated$`).
    //
    // Drop the merged file from `filePathsWithRemoteChanges$` so the pull bar reflects the current
    // state. The manual pull dialog already clears the whole set on success, but an auto-merge has
    // no equivalent hook — without this the bar stays lit even after the only file with remote
    // changes was successfully merged.
    this.workspaceSession.on('workspace.doc.merged', (event: CollabDocMergeEvent) => {
      this.dropFromRemoteChanges(event.filePath);
      this.docMergedSubject.next(event);
    });

    this.workspaceSession.on('workspace.file.renamed', (event: CollabDocRenameEvent) => {
      const existing = this.workspaceFilesService.getWorkspaceFile(event.projectId, event.branchName, event.oldFilePath);
      if (existing) {
        this.workspaceFilesService.removeItems$([existing]);
        // If the renamed doc is still pending-add, a workspace_file row remains for the new
        // path on the server. Mirror that locally by re-adding the old row under the new path.
        // For committed renames (gitStatus !== 'added'), the server removes the row entirely.
        if (event.gitStatus === 'added') {
          this.workspaceFilesService.addItems$([{ ...existing, docId: event.newDocId, filePath: event.newFilePath }]);
        }
      }

      this.fileRenamedSubject.next(event);
    });

    this.workspaceSession.on('workspace.pull.started', (state: WorkspacePullState) => {
      this.pullStartedSubject.next(state);
    });

    this.workspaceSession.on('workspace.pull.progress', (state: WorkspacePullState) => {
      this.pullProgressSubject.next(state);
    });

    this.workspaceSession.on('workspace.pull.completed', (state: WorkspacePullState) => {
      this.pullCompletedSubject.next(state);
    });

    this.workspaceSession.on('workspace.pull.failed', (state: WorkspacePullState) => {
      this.pullFailedSubject.next(state);
    });

    this.workspaceSession.on('workspace.pull.file-completed', (data: { filePath: string; fileOutcome: WorkspacePullFileState }) => {
      this.pullFileCompletedSubject.next(data);
    });

    this.workspaceSession.on('workspace.files.loaded', (data: WorkspaceFilesGetResponse) => {
      // Remove the current workspace files from the data store. This is necessary because some files may have been deleted.
      const items = this.workspaceFilesService.getItemsByProperties('projectId', projectId, 'branchName', branchName);
      this.workspaceFilesService.removeItems$(items);

      // Add the files to the data store
      this.workspaceFilesService.addItems$(data.workspaceFiles);

      // Bulk load all the users
      this.loadUsers(data.workspaceFiles);

      // Workspace files loaded successfully — clear loading and any errors
      this.workspaceLoadingState.update(false);

      // tell workspace grid that it is ready.
      this.workspaceFilesService.workspaceItemsLoaded.next('loaded');
    });

    // Start the workspace session
    this.workspaceSession.start();
  }

  private destroyWorkspaceSession() {
    this.errorSubscription?.unsubscribe();
    this.connectedSubscription?.unsubscribe();
    this.autoFetchSubscription?.unsubscribe();
    this.workspaceLoadingState.update(false);
    // filePathsWithRemoteChanges$ is owned by reset()/clear(); leaving it untouched here lets a same-
    // params reset() preserve the bar's visibility across the destroy → create cycle.
    this.activeParams$.next(null);

    if (this.workspaceSession) {
      this.workspaceSession.stop();
      this.workspaceSession = null;
    }
  }

  private dropFromRemoteChanges(filePath: string) {
    const current = this.filePathsWithRemoteChanges$.value;
    if (current.has(filePath)) {
      const next = new Map(current);
      next.delete(filePath);
      this.filePathsWithRemoteChanges$.next(next);
    }
  }

  private loadUsers(workspaceFiles: WorkspaceFile[]) {
    const userIds = new Set<string>();

    workspaceFiles.forEach(workspaceFile => {
      workspaceFile.userStatuses?.forEach(userStatus => {
        userIds.add(userStatus.userId)
      });
    });

    if (userIds.size > 0 && this.licenseId !== undefined) {
      this.licenseUsersService.loadLicenseUsersByUserId$(this.licenseId, Array.from(userIds)).subscribe();
    }
  }
}
